// The status page's two probes: the server over HTTP and the socket over the
// WebSocket path. Both go to this page's own origin (plan §2.1), so a green
// page on vtt-dev proves nginx routes both paths. The socket is refused
// without a session, by design — so the probe reports "reachable" when the
// refusal arrived through the WebSocket path, and "no answer" when nothing did.
import { io } from 'socket.io-client';

export interface ServerHealth {
  ok: boolean;
  commit: string;
  db: 'ok' | 'error';
  schema?: string;
}

export async function fetchHealth(fetchImpl: typeof fetch = fetch): Promise<ServerHealth | null> {
  try {
    const res = await fetchImpl('/api/health', { headers: { Accept: 'application/json' } });
    const body: unknown = await res.json();
    if (typeof body !== 'object' || body === null) return null;
    const b = body as Record<string, unknown>;
    if (typeof b.ok !== 'boolean' || typeof b.commit !== 'string') return null;
    return {
      ok: b.ok,
      commit: b.commit,
      db: b.db === 'ok' ? 'ok' : 'error',
      ...(typeof b.schema === 'string' ? { schema: b.schema } : {}),
    };
  } catch {
    return null;
  }
}

export type SocketProbe = 'reachable' | 'no-answer';

/** Resolves once the socket path answered — with the expected refusal of an anonymous handshake — or never did. */
export function probeSocket(timeoutMs = 8000, connect: typeof io = io): Promise<SocketProbe> {
  return new Promise((resolve) => {
    const socket = connect({ transports: ['websocket'], reconnection: false, auth: {} });
    const done = (value: SocketProbe) => {
      clearTimeout(timer);
      socket.close();
      resolve(value);
    };
    const timer = setTimeout(() => done('no-answer'), timeoutMs);
    socket.on('connect', () => done('reachable'));
    socket.on('connect_error', (err) => done(err.message === 'unauthorized' ? 'reachable' : 'no-answer'));
  });
}
